import { ReactNode } from 'react';
import { ChevronSmall } from '../../assets/svg';
import { useMeasuredHeight } from '../../hooks/useMeasuredHeight';
import { taxRateAmountLabel } from '../../helpers/taxRates';
import {
  LocationTaxRateEntity,
  TaxRateEntity,
  TaxRateSource,
} from '../../types/taxRate.types';
// Direct file imports on purpose - the styles barrel triggers a circular init.
import { CollapsibleComponent, Div, Icon, Pill } from '../../styles/styles';
import { Disclaimer, H5, P } from '../../styles/text';
import { lightTheme } from '../../styles/theme';

/** Side padding of the row, in px - the collapsible cancels it out below. */
const ROW_PADDING_X = 20;
/** Top and bottom padding, reused as the gap above the first separator. */
const ROW_PADDING_Y = 12;

type Props = {
  /**
   * Only the fields the row shows are required, so a rate that does not exist
   * yet - a draft on the verification form - renders the same as a saved one.
   */
  taxRate: Pick<TaxRateEntity, 'name' | 'rate' | 'type' | 'applyToAllItems'> &
    Partial<Pick<TaxRateEntity, 'source'>> &
    Partial<Pick<LocationTaxRateEntity, 'assignments'>>;
  /** Goes before the name. The pickers put their radio here. */
  lead?: ReactNode;
  /** Goes after the amount. The location screen puts edit / delete here. */
  actions?: ReactNode;
  /** How many items the rate is set on. Only for `applyToAllItems: false`. */
  showAssignmentCount?: boolean;
  onClick?: () => void;
  /**
   * Pass all three to make the row expand inside its own box, the way the
   * payout cards do - `children` is what appears once it is open.
   */
  expanded?: boolean;
  onToggleExpanded?: () => void;
  children?: ReactNode;
};

/**
 * One tax rate as a row: name, what it charges, and the two facts that decide
 * whether it can be touched at all - whether it applies to everything, and
 * whether it came from the POS.
 *
 * Shared by the location's list and by the menu's item and option pickers so
 * the same rate reads the same way on all three.
 */
export const TaxRateRow = ({
  taxRate,
  lead,
  actions,
  showAssignmentCount = false,
  onClick,
  expanded = false,
  onToggleExpanded,
  children,
}: Props) => {
  const fromPos = taxRate.source === TaxRateSource.pos;
  const assignmentCount = taxRate.assignments?.length ?? 0;
  const { ref: contentRef, height: contentHeight } =
    useMeasuredHeight<HTMLDivElement>();

  return (
    <Div
      $box
      $flex
      $column
      $p={`${ROW_PADDING_Y}px ${ROW_PADDING_X}px`}
      $mb="8"
    >
      <Div
        $flex
        $alignCenter
        $between
        $gap="16px"
        $gapSM="8px"
        $pointer={!!onClick || !!onToggleExpanded}
        onClick={onClick ?? onToggleExpanded}
      >
        <Div $flex $alignCenter $gap="12px" $gapSM="8px">
          {lead}
          <Div $flex $column $gap="2px">
            <P $m="0" $left $bold>
              {taxRate.name}
            </P>
            <Div $flex $alignCenter $gap="8px" $gapSM="6px" $wrap>
              <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
                {taxRate.applyToAllItems
                  ? 'Applies to all items'
                  : showAssignmentCount
                  ? `Set on ${assignmentCount} ${
                      assignmentCount === 1 ? 'item' : 'items'
                    }`
                  : 'Only where it is set'}
              </Disclaimer>
              {fromPos && (
                <Pill
                  $background={lightTheme.gray}
                  $minWidth="0"
                  $padding="1px 10px"
                >
                  <Disclaimer $m="0" $color={lightTheme.darkGray}>
                    From POS
                  </Disclaimer>
                </Pill>
              )}
            </Div>
          </Div>
        </Div>

        <Div $flex $alignCenter $gap="8px" $gapSM="6px">
          <H5 $m="0" $color={lightTheme.primary}>
            {taxRateAmountLabel(taxRate)}
          </H5>
          {actions}
          {onToggleExpanded && (
            <Icon
              $pointer
              $rotate={expanded ? '180deg' : '0'}
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpanded();
              }}
            >
              <ChevronSmall />
            </Icon>
          )}
        </Div>
      </Div>

      {/* Stretched into the row's own padding so the separators inside run the
      full width of the box, the way they do under a modifier group. */}
      {onToggleExpanded && (
        <CollapsibleComponent
          expanded={expanded}
          elementHeight={contentHeight}
          // Only applied while open. The header sits inside the box's padding
          // but its own bottom is flush, so without this the first separator
          // hugs the meta line; the row's own padding is the matching gap.
          marginTop={`${ROW_PADDING_Y}px`}
          style={{
            marginLeft: `-${ROW_PADDING_X}px`,
            marginRight: `-${ROW_PADDING_X}px`,
          }}
        >
          <div ref={contentRef}>{children}</div>
        </CollapsibleComponent>
      )}
    </Div>
  );
};
